import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/Layout";

const LAST_UPDATED = "9 June 2026";

export default function Terms() {
  return (
    <Layout>
      <Helmet>
        <title>Terms of Service | IndiaBusinessCircle</title>
        <meta name="description" content="The terms governing your use of IndiaBusinessCircle, India's verified B2B networking platform — including account rules, GST verification, networking conduct, and subscriptions." />
        <link rel="canonical" href={`${window.location.origin}/terms`} />
      </Helmet>

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Legal</span>
          <h1 data-testid="terms-title" className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">Terms of Service</h1>
          <p className="mt-3 text-slate-500 text-sm">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-5 sm:px-8 py-10">
        <p className="text-slate-600 leading-relaxed">
          These Terms of Service ("Terms") govern your access to and use of IndiaBusinessCircle ("IBC", "we", "us"),
          a verified business networking platform for Indian businesses. By creating an account or using IBC, you
          agree to these Terms. If you do not agree, please do not use the platform.
        </p>

        <Section title="1. Eligibility & Accounts">
          <ul>
            <li>You must be at least 18 years old and authorised to represent the business you register.</li>
            <li>You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</li>
            <li>You agree to provide accurate, current, and complete information and to keep it updated.</li>
          </ul>
        </Section>

        <Section title="2. Business Verification & GST">
          <p>
            IBC displays a "GST Verified" badge based on a format-level validity check of the GST Identification Number
            (GSTIN) you provide. This is a trust signal and <strong>not</strong> an official government certification or
            legal confirmation of a business's status. You warrant that any GSTIN and business details you submit are
            genuine and that you are entitled to use them. Submitting false or fraudulent information may result in
            suspension or removal.
          </p>
        </Section>

        <Section title="3. Networking Conduct">
          <p>When using discovery, follows, connections, messaging, posts, and comments, you agree not to:</p>
          <ul>
            <li>Send spam, unsolicited bulk offers, or repetitive connection/message requests.</li>
            <li>Harass, defame, threaten, or discriminate against other users.</li>
            <li>Misrepresent your identity, business, affiliations, or offerings.</li>
            <li>Post unlawful, infringing, obscene, or misleading content.</li>
            <li>Scrape, harvest, or misuse other members' data, including contact details revealed through connections.</li>
          </ul>
          <p>
            Contact details and messaging are intended for legitimate B2B purposes only. Connections are consent-based;
            messaging is available only between businesses that have accepted a connection.
          </p>
        </Section>

        <Section title="4. Content & Ownership">
          <p>
            You retain ownership of the content you post (profile details, media, posts, articles you are authorised to
            publish). By posting, you grant IBC a non-exclusive, worldwide, royalty-free licence to host, display, and
            distribute that content for the purpose of operating and promoting the platform. You are solely responsible
            for the content you publish and must have the rights to share it.
          </p>
        </Section>

        <Section title="5. Subscriptions, Credits & Payments">
          <ul>
            <li>Certain features (such as posting to the feed) consume credits. New accounts receive a limited number of free trial credits.</li>
            <li>Paid plans provide credits on a recurring basis and are billed through our payment partner. Prices are displayed in INR and are inclusive of applicable taxes unless stated otherwise.</li>
            <li>Subscriptions renew automatically until cancelled. Except where required by law, payments are non-refundable.</li>
            <li>We may change plans, pricing, or credit allocations with reasonable notice.</li>
          </ul>
        </Section>

        <Section title="6. Moderation & Suspension">
          <p>
            We may review, hide, or remove content and may suspend or terminate accounts that violate these Terms,
            are reported, or pose a risk to the community. Suspended businesses are removed from public directories,
            discovery, and feeds.
          </p>
        </Section>

        <Section title="7. Disclaimers">
          <p>
            IBC is provided on an "as is" and "as available" basis. We do not guarantee the accuracy of any business
            information, the outcome of any connection, or the conduct of any user. We are not a party to any dealings,
            transactions, or contracts you enter into with other businesses on the platform.
          </p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, IBC and its affiliates will not be liable for any indirect,
            incidental, special, or consequential damages, or for any loss of profits, revenue, data, or business
            opportunities arising from your use of the platform.
          </p>
        </Section>

        <Section title="9. Indemnity">
          <p>
            You agree to indemnify and hold harmless IBC from any claims, damages, or expenses arising out of your
            content, your use of the platform, or your violation of these Terms or any applicable law.
          </p>
        </Section>

        <Section title="10. Governing Law">
          <p>
            These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction
            of the competent courts in India.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may update these Terms from time to time. Material changes will be reflected by the "Last updated" date
            above. Continued use of IBC after changes constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>
            Questions about these Terms? Contact us at{" "}
            <a href="mailto:support@indiabusinesscircle.com" className="text-brand font-semibold underline">support@indiabusinesscircle.com</a>.
          </p>
        </Section>
      </article>
    </Layout>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-display font-bold text-xl text-slate-900 mb-3">{title}</h2>
    <div className="text-slate-600 leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">
      {children}
    </div>
  </section>
);
